import React, { useState, useEffect, useMemo } from "react"
import { Row, Col, Card, CardBody } from "reactstrap"
import axios from "axios"
import configure from "configure"
import Loader from "../../Separate/Loader"
import { calculateFinancialYearTotals } from "utils/common-helpers"
import { fetchNodeData } from "utils/common-fetchData"
import { DatePicker } from "antd"
import moment from "moment"
import secureLocalStorage from "react-secure-storage"

const Projection = () => {
  const [locations, setLocations] = useState([])
  const [datas, setDatas] = useState([])
  const [filteredContracts, setFilteredContracts] = useState([])
  const [selectedLocId, setSelectedLocId] = useState(1)
  const [isLoading, setLoading] = useState(false)
  const [invoices, setInvoices] = useState([])
  const [filteredInvoices, setFilteredInvoices] = useState([])

  const { RangePicker } = DatePicker

  const [dateRange, setDateRange] = useState([
    moment().startOf("month"),
    moment().endOf("month"),
  ])

  const token = secureLocalStorage.getItem("auth_token")

  // Fetch contracts
  const fetchData = async () => {
    setLoading(true)
    try {
      const locRes = await axios.get(`${configure.nodeApi}/locations`)
      setLocations(locRes.data.data || [])

      const formData = new FormData()
      formData.append("user_id", localStorage.getItem("auth_id"))

      const conRes = await axios({
        data: formData,
        method: "post",
        url: `${configure.nodeApi}/contract`,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })

      setDatas(conRes.data.data.contract || [])
    } catch (err) {
      console.error("Fetch error:", err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch invoices
  const fetchInvoiceData = async () => {
    const response = await fetchNodeData(setLoading, `ztplInvoice`, token)
    if (response?.status === 200) {
      setInvoices(response?.data || [])
    }
  }

  useEffect(() => {
    fetchData()
    fetchInvoiceData()
  }, [])

  // Default filtering on location change
  useEffect(() => {
    setFilteredContracts(
      datas
        .filter(item => Number(item.location) === Number(selectedLocId))
        .filter(item => item.signature === "signed")
    )
    console.log(filteredContracts)
    setFilteredInvoices(
      invoices.filter(item => Number(item.location) === Number(selectedLocId))
    )
  }, [datas, invoices, selectedLocId])

  // Stats for contracts
  const stats = useMemo(() => {
    const symbol =
      filteredContracts.length > 0 ? filteredContracts[0].currency_symbol : "₹"
    const totalValue = filteredContracts.reduce((sum, item) => {
      // parseFloat ensures the string "1000.00" becomes a number
      const val = parseFloat(item.value || 0)
      const safeVal = isNaN(val) ? 0 : val
      //   console.log(val);
      return sum + safeVal
    }, 0)

    return {
      projectionValue: `${symbol} ${totalValue.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}`,
      contractCount: filteredContracts.length,
      //   projectionValue: filteredContracts.length,
      //   invoiced: `${symbol} ${financialTotals.totalAmount}`,
      //   received: `${symbol} ${financialTotals.receivedAmount}`
    }
  }, [filteredContracts])

  // Stats for invoices
  const invoiceStats = useMemo(() => {
    const symbol =
      filteredInvoices.length > 0 ? filteredInvoices[0].currency_symbol : "₹"
    const totalInvoiced = filteredInvoices.reduce(
      (sum, inv) => sum + parseFloat(inv.total || 0),
      0
    )
    const totalReceived = filteredInvoices.reduce(
      (sum, inv) => sum + parseFloat(inv.paid_amount || 0),
      0
    )
    const totalPending = filteredInvoices.reduce(
      (sum, inv) => sum + parseFloat(inv.balance_due || 0),
      0
    )

    return {
      invoiced: `${symbol} ${totalInvoiced.toFixed(2)}`,
      received: `${symbol} ${totalReceived.toFixed(2)}`,
      pending: `${symbol} ${totalPending.toFixed(2)}`,
    }
  }, [filteredInvoices])

  // ✅ Filter function
  const applyFilters = (startDate, endDate, locationId) => {
    // 1. Convert selected Range to plain strings "YYYY-MM-DD"
    const startStr = startDate.format("YYYY-MM-DD")
    const endStr = endDate.format("YYYY-MM-DD")

    // 2. Filter contracts
    const filteredContractsByDate = datas
      .filter(item => Number(item.location) === Number(locationId))
      .filter(item => {
        // Convert API ISO string to "YYYY-MM-DD"
        const contractDateStr = moment(item.created_at).format("YYYY-MM-DD")

        // Check if it sits within the string range
        return contractDateStr >= startStr && contractDateStr <= endStr
      })
    setFilteredContracts(filteredContractsByDate)

    // 3. Filter invoices
    const filteredInvoicesByDate = invoices
      .filter(item => Number(item.location) === Number(locationId))
      .filter(item => {
        // Convert API ISO string to "YYYY-MM-DD"
        const invoiceDateStr = moment(
          item.invoice_date || item.created_at
        ).format("YYYY-MM-DD")

        // Check if it sits within the string range
        return invoiceDateStr >= startStr && invoiceDateStr <= endStr
      })
    setFilteredInvoices(filteredInvoicesByDate)
  }
  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <h5 className="text-purple fw-bold mb-4">
            Projection Current Finance
          </h5>

          {/* Location + Date Filter Row */}
          <Row className="mb-4 align-items-center">
            {/* Location Filters */}
            <Col className="d-flex gap-3 flex-wrap">
              {locations.map(loc => (
                <div
                  key={loc.id}
                  onClick={() => setSelectedLocId(loc.id)}
                  className={`px-4 py-2  shadow-sm border d-flex align-items-center gap-2 ${
                    selectedLocId === loc.id
                      ? "bg-purple text-white"
                      : "bg-white text-purple"
                  }`}
                  style={{ cursor: "pointer", transition: "0.3s" }}
                >
                  <span className="fw-bold">{loc.location}</span>
                </div>
              ))}
            </Col>

            {/* Date Picker */}
            <Col md={6} className="d-flex justify-content-end gap-2">
              <RangePicker
                className="col-md-6"
                value={dateRange}
                format="DD-MM-YYYY"
                onChange={dates => {
                  // Ant Design sends null if cleared, so we check for dates
                  if (dates) {
                    setDateRange([dates[0], dates[1]])
                  }
                }}
                style={{ height: "38px" }}
              />

              <button
                className="btn btn-sm bg-purple text-white"
                onClick={() => {
                  setLoading(true)

                  // Pass cloned dates to avoid mutation
                  applyFilters(
                    dateRange[0].clone(),
                    dateRange[1].clone(),
                    selectedLocId
                  )

                  setTimeout(() => setLoading(false), 300)
                }}
              >
                <i className="bi bi-funnel-fill fs-5"></i>
              </button>
            </Col>
          </Row>

          {/* Cards */}
          {isLoading ? (
            <Loader />
          ) : (
            <Row>
              <Col md={4}>
                <Card className="bg-light border-0 shadow-sm">
                  <CardBody className="text-center">
                    <h6 className="text-muted mb-2">Total Projection</h6>
                    <h4 className="text-purple fw-bold mb-1">
                      {stats.projectionValue}
                    </h4>
                  </CardBody>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="bg-light border-0 shadow-sm">
                  <CardBody className="text-center">
                    <h6 className="text-muted mb-2">Total Invoiced</h6>
                    <h4 className="text-primary fw-bold">
                      {invoiceStats.invoiced}
                    </h4>
                  </CardBody>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="bg-light border-0 shadow-sm">
                  <CardBody className="text-center">
                    <h6 className="text-muted mb-2">Actual Received</h6>
                    <h4 className="text-success fw-bold">
                      {invoiceStats.received}
                    </h4>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>
    </React.Fragment>
  )
}

export default Projection
